import { useState, useEffect, useMemo, useCallback } from "react";
import { Routes, Route } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Edit, Trash2, Search, Package, ArrowUpCircle, ArrowDownCircle,
  AlertTriangle, ShoppingCart, History, Save, X, Filter, FileText, Wrench,
} from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";
import { RelatorioEstoqueDialog } from "@/components/estoque/RelatorioEstoqueDialog";
import { EntradaNFForm, SaidaAjustePanel } from "@/components/estoque/EntradaNFForm";

const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const fmtQtd = (q) => (q === null || q === undefined ? "—" : Number(q).toLocaleString("pt-BR", { maximumFractionDigits: 2 }));
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("pt-BR") : "-");
const fmtDateTime = (s) =>
  s
    ? new Date(s).toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "-";

const authHeaders = () => {
  const token =
    sessionStorage.getItem("luvcorp_token");
  return { Authorization: `Bearer ${token}` };
};

const emptyMaterial = () => ({
  codigo: "",
  nome: "",
  descricao: "",
  tipo_material: "Outros",
  controle: "consumivel",
  unidade_medida_id: "",
  quantidade_minima: 0,
  quantidade_atual: 0,
  valor_unitario: 0,
  fornecedor_id: "",
  fornecedor_secundario_id: "",
  localizacao: "",
  observacoes: "",
});

// ============================================================
// PÁGINA PRINCIPAL — Tabs pattern (igual ao Precificação)
// ============================================================
function EstoqueMain({ modo = "consumivel" }) {
  const ehFerramentas = modo === "retornavel";
  // Estado de dados (compartilhado entre abas)
  const [items, setItems] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [obras, setObras] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("lista");

  // Form de cadastro/edição
  const [form, setForm] = useState(emptyMaterial());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Filtros lista
  const [search, setSearch] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroCritico, setFiltroCritico] = useState(false);
  const [selIds, setSelIds] = useState(new Set());

  // Modal Nova Unidade
  const [showUnidadeDialog, setShowUnidadeDialog] = useState(false);
  const [showRelatorio, setShowRelatorio] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = authHeaders();
      const [itemsRes, alertasRes, unidadesRes, fornRes, obrasRes, tiposRes, usersRes] =
        await Promise.all([
          axios.get(`${API}/estoque`, { headers }),
          axios.get(`${API}/estoque/alertas/minimo`, { headers }),
          axios.get(`${API}/unidades-medida`, { headers }),
          axios.get(`${API}/clientes-fornecedores?tipo=fornecedor`, { headers }),
          axios.get(`${API}/estoque/obras-ativas`, { headers }),
          axios.get(`${API}/estoque/tipos`, { headers }),
          axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
        ]);
      setItems(itemsRes.data);
      setAlertas(alertasRes.data);
      setUnidades(unidadesRes.data);
      setFornecedores(fornRes.data);
      setObras(obrasRes.data);
      setTipos(tiposRes.data);
      setUsers(usersRes.data || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao carregar estoque"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    const base = items.filter((i) =>
      ehFerramentas ? i.controle === "retornavel" : (i.controle || "consumivel") !== "retornavel");
    return base.filter((i) => {
      if (search) {
        const q = search.toLowerCase();
        const hit =
          i.nome?.toLowerCase().includes(q) ||
          i.codigo?.toLowerCase().includes(q) ||
          i.tipo_material?.toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (filtroTipo && i.tipo_material !== filtroTipo) return false;
      if (filtroCritico && i.quantidade_atual >= i.quantidade_minima) return false;
      return true;
    });
  }, [items, search, filtroTipo, filtroCritico, ehFerramentas]);

  const startNovo = () => {
    setForm({ ...emptyMaterial(), controle: modo });
    setEditingId(null);
    setTab("cadastro");
  };

  const startEditar = (item) => {
    setForm({
      codigo: item.codigo || "",
      nome: item.nome || "",
      descricao: item.descricao || "",
      tipo_material: item.tipo_material || "Outros",
      controle: item.controle || "consumivel",
      unidade_medida_id: item.unidade_medida_id || "",
      quantidade_minima: item.quantidade_minima || 0,
      quantidade_atual: item.quantidade_atual || 0,
      valor_unitario: item.valor_unitario || 0,
      fornecedor_id: item.fornecedor_id || "",
      fornecedor_secundario_id: item.fornecedor_secundario_id || "",
      localizacao: item.localizacao || "",
      observacoes: item.observacoes || "",
    });
    setEditingId(item.id);
    setTab("cadastro");
  };

  const handleDelete = async (id) => {
    if (!confirm("Excluir este material? Todas as movimentações ficarão arquivadas.")) return;
    try {
      await axios.delete(`${API}/estoque/${id}`, { headers: authHeaders() });
      toast.success("Material excluído");
      await fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao excluir"));
    }
  };

  // ----- Validação cliente do código (4 chars alfanuméricos) -----
  const codigoErro = useMemo(() => {
    if (editingId) return null; // não muda em edição
    const v = (form.codigo || "").trim().toUpperCase();
    if (!v) return null;
    if (v.length !== 4) return "Código deve ter exatamente 4 caracteres";
    if (!/^[A-Z0-9]+$/.test(v)) return "Use somente letras e números";
    if (items.some((m) => (m.codigo || "").toUpperCase() === v)) return "Código já cadastrado";
    return null;
  }, [form.codigo, items, editingId]);

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!editingId) {
      if (!form.codigo || codigoErro) {
        toast.error(codigoErro || "Código obrigatório (4 caracteres)");
        return;
      }
    }
    if (!form.nome.trim()) return toast.error("Nome do material é obrigatório");
    if (!form.unidade_medida_id) return toast.error("Unidade de medida é obrigatória");
    if (!form.tipo_material) return toast.error("Tipo de material é obrigatório");
    if (!form.fornecedor_id) return toast.error("Fornecedor principal é obrigatório");
    if (form.valor_unitario <= 0) return toast.error("Preço unitário deve ser maior que zero");
    if (form.quantidade_minima < 0) return toast.error("Estoque mínimo não pode ser negativo");

    setSaving(true);
    try {
      const headers = authHeaders();
      if (editingId) {
        // PUT exclui codigo e quantidade_atual (não editáveis)
        const { codigo: _c, quantidade_atual: _q, ...payload } = form;
        await axios.put(`${API}/estoque/${editingId}`, payload, { headers });
        toast.success("Material atualizado");
      } else {
        await axios.post(`${API}/estoque`, {
          ...form,
          codigo: form.codigo.toUpperCase().trim(),
        }, { headers });
        toast.success("Material cadastrado");
      }
      await fetchData();
      setForm(emptyMaterial());
      setEditingId(null);
      setTab("lista");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao salvar material"));
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <MainLayout>
        <div className="p-8 text-center text-slate-500">Carregando estoque...</div>
      </MainLayout>
    );

  const itensModo = items.filter((i) =>
    ehFerramentas ? i.controle === "retornavel" : (i.controle || "consumivel") !== "retornavel");
  const alertasModo = alertas.filter((a) =>
    ehFerramentas ? a.controle === "retornavel" : (a.controle || "consumivel") !== "retornavel");
  const valorTotalEstoque = itensModo.reduce(
    (sum, i) => sum + ((i.quantidade_atual || 0) + (ehFerramentas ? (i.em_obras || 0) : 0)) * (i.valor_unitario || 0),
    0
  );
  const totalNaBase = itensModo.reduce((s, i) => s + (i.quantidade_atual || 0), 0);
  const totalEmObras = itensModo.reduce((s, i) => s + (i.em_obras || 0), 0);

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="page-estoque">
        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              {ehFerramentas ? <Wrench className="h-7 w-7 text-luvcorp-navy" /> : <Package className="h-7 w-7 text-luvcorp-navy" />}
              {ehFerramentas ? "Ferramentas" : "Estoque de Materiais"}
            </h1>
            <p className="text-slate-500 text-sm">
              {ehFerramentas
                ? "Ferramentas miúdas retornáveis — controle por quantidade, na base e em obras."
                : "Controle integrado de materiais, entradas, saídas e alocação por obra."}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowRelatorio(true)} data-testid="btn-relatorio-estoque">
              <FileText className="h-4 w-4 mr-2" />
              Relatório PDF
            </Button>
            <Button variant="outline" onClick={() => setShowUnidadeDialog(true)} data-testid="btn-nova-unidade">
              <Plus className="h-4 w-4 mr-2" />
              Nova Unidade
            </Button>
            <Button onClick={startNovo} className="bg-luvcorp-navy hover:bg-luvcorp-navy/90 text-white" data-testid="btn-novo-material">
              <Plus className="h-4 w-4 mr-2" />
              Novo Material
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {ehFerramentas ? (
            <>
              <KPI icon={Wrench} label="Ferramentas cadastradas" value={itensModo.length} color="blue" />
              <KPI icon={Package} label="Quantidade na base" value={fmtQtd(totalNaBase)} color="purple" />
              <KPI icon={History} label="Quantidade em obras" value={fmtQtd(totalEmObras)} color="red" />
              <KPI icon={ShoppingCart} label="Valor total (base + obras)" value={fmtBRL(valorTotalEstoque)} color="green" small />
            </>
          ) : (
            <>
              <KPI icon={Package} label="Materiais cadastrados" value={itensModo.length} color="blue" />
              <KPI icon={AlertTriangle} label="Abaixo do mínimo" value={alertasModo.length} color="red" />
              <KPI icon={ShoppingCart} label="Valor total em estoque" value={fmtBRL(valorTotalEstoque)} color="green" small />
              <KPI icon={History} label="Tipos de material" value={tipos.length} color="purple" />
            </>
          )}
        </div>

        {/* ALERTA */}
        {alertasModo.length > 0 && (
          <Card className="border-l-4 border-l-red-500 shadow-none border-red-200" data-testid="alerta-estoque-baixo">
            <CardContent className="p-3 flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <div className="text-sm">
                <span className="font-semibold text-red-700">{alertasModo.length} {ehFerramentas ? "ferramentas" : "materiais"}</span>{" "}
                abaixo do estoque mínimo:&nbsp;
                <span className="text-slate-600">
                  {alertasModo.slice(0, 5).map((a) => a.nome).join(", ")}
                  {alertasModo.length > 5 && ` e mais ${alertasModo.length - 5}…`}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* TABS */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="lista" data-testid="tab-lista">Materiais</TabsTrigger>
            <TabsTrigger value="cadastro" data-testid="tab-cadastro">
              {editingId ? "Editar material" : "Cadastro"}
            </TabsTrigger>
            <TabsTrigger value="movimentacoes" data-testid="tab-movimentacoes">Movimentações</TabsTrigger>
            <TabsTrigger value="historico" data-testid="tab-historico">Histórico</TabsTrigger>
          </TabsList>

          {/* ============================== LISTA ============================== */}
          <TabsContent value="lista" className="space-y-3">
            <Card>
              <CardContent className="p-3 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[260px]">
                  <Label className="text-xs">Buscar</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Nome, código ou tipo..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9"
                      data-testid="input-busca-material"
                    />
                  </div>
                </div>
                <div className="w-[200px]">
                  <Label className="text-xs">Tipo</Label>
                  <NativeSelect
                    value={filtroTipo}
                    onValueChange={setFiltroTipo}
                    options={[
                      { value: "", label: "Todos os tipos" },
                      ...tipos.map((t) => ({ value: t, label: t })),
                    ]}
                  />
                </div>
                <div className="flex items-end pb-[2px]">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filtroCritico}
                      onChange={(e) => setFiltroCritico(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300"
                      data-testid="filtro-critico"
                    />
                    <span className="text-slate-700">Somente críticos</span>
                  </label>
                </div>
                {(search || filtroTipo || filtroCritico) && (
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => { setSearch(""); setFiltroTipo(""); setFiltroCritico(false); }}
                    data-testid="btn-limpar-filtros"
                  >
                    <X className="h-4 w-4 mr-1" /> Limpar
                  </Button>
                )}
              </CardContent>
            </Card>

            {selIds.size > 0 && (
              <Card className="border-l-4 border-l-luvcorp-gold">
                <CardContent className="p-3 flex flex-wrap items-center gap-2" data-testid="barra-lote">
                  <span className="text-sm font-medium">{selIds.size} selecionado(s) — marcar como:</span>
                  {ehFerramentas ? (
                    <Button size="sm" variant="outline" data-testid="btn-lote-consumivel" onClick={async () => {
                      try {
                        const r = await axios.post(`${API}/estoque/controle-lote`, { ids: [...selIds], controle: "consumivel" }, { headers: authHeaders() });
                        toast.success(r.data.message + " — movido para Estoque de Materiais"); setSelIds(new Set()); fetchData();
                      } catch (err) { toast.error(extractErrorMessage(err, "Erro ao alterar controle")); }
                    }}>Consumível</Button>
                  ) : (
                    <Button size="sm" variant="outline" data-testid="btn-lote-retornavel" onClick={async () => {
                      try {
                        const r = await axios.post(`${API}/estoque/controle-lote`, { ids: [...selIds], controle: "retornavel" }, { headers: authHeaders() });
                        toast.success(r.data.message + " — movido para a tela Ferramentas"); setSelIds(new Set()); fetchData();
                      } catch (err) { toast.error(extractErrorMessage(err, "Erro ao alterar controle")); }
                    }}>Retornável</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setSelIds(new Set())}>Limpar seleção</Button>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">
                        <input type="checkbox" className="h-4 w-4 rounded border-slate-300" data-testid="check-todos"
                          checked={filtered.length > 0 && filtered.every((i) => selIds.has(i.id))}
                          onChange={(e) => setSelIds(e.target.checked ? new Set(filtered.map((i) => i.id)) : new Set())} />
                      </TableHead>
                      <TableHead>Código</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Na base</TableHead>
                      {ehFerramentas && <TableHead className="text-right">Em obras</TableHead>}
                      <TableHead className="text-right">Mínimo</TableHead>
                      <TableHead className="text-right">Preço Unit.</TableHead>
                      <TableHead>Unidade</TableHead>
                      <TableHead>Fornecedor</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={ehFerramentas ? 11 : 10} className="text-center py-12 text-slate-400">
                          {items.length === 0 ? "Nenhum material cadastrado. Use o botão Novo Material." : "Nenhum resultado para os filtros aplicados."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.map((it) => {
                        const critico = it.quantidade_atual < it.quantidade_minima;
                        return (
                          <TableRow key={it.id} className={critico ? "bg-red-50/60" : ""} data-testid={`row-mat-${it.id}`}>
                            <TableCell>
                              <input type="checkbox" className="h-4 w-4 rounded border-slate-300" data-testid={`check-mat-${it.id}`}
                                checked={selIds.has(it.id)}
                                onChange={(e) => setSelIds((s) => { const n = new Set(s); e.target.checked ? n.add(it.id) : n.delete(it.id); return n; })} />
                            </TableCell>
                            <TableCell className="font-mono font-semibold">{it.codigo}</TableCell>
                            <TableCell className="font-medium">{it.nome}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{it.tipo_material || "—"}</Badge>
                            </TableCell>
                            <TableCell className={`text-right font-bold ${critico ? "text-red-600" : "text-slate-900"}`}>
                              {fmtQtd(it.quantidade_atual)}
                              {critico && <AlertTriangle className="inline h-3 w-3 ml-1 -mt-1" />}
                            </TableCell>
                            {ehFerramentas && (
                              <TableCell className="text-right text-teal-700 font-medium">{fmtQtd(it.em_obras)}</TableCell>
                            )}
                            <TableCell className="text-right text-slate-600">{fmtQtd(it.quantidade_minima)}</TableCell>
                            <TableCell className="text-right">{fmtBRL(it.valor_unitario)}</TableCell>
                            <TableCell className="text-xs text-slate-600">
                              {it.unidade?.sigla || it.unidade?.descricao || "—"}
                            </TableCell>
                            <TableCell className="text-xs text-slate-600">{it.fornecedor_nome || "—"}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="sm" title="Editar" onClick={() => startEditar(it)} data-testid={`btn-editar-${it.id}`}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" title="Excluir" onClick={() => handleDelete(it.id)} data-testid={`btn-deletar-${it.id}`}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============================== CADASTRO ============================== */}
          <TabsContent value="cadastro" className="space-y-3">
            <form onSubmit={handleSave} className="space-y-4">
              <Card>
                <CardContent className="p-4 space-y-4">
                  <h3 className="text-sm font-semibold text-luvcorp-navy uppercase tracking-wide flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-luvcorp-navy text-white text-xs flex items-center justify-center">1</span>
                    Informações básicas
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <Label>Código <span className="text-red-500">*</span></Label>
                      <Input
                        value={form.codigo}
                        onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase().slice(0, 4) })}
                        placeholder="TINT"
                        maxLength={4}
                        disabled={!!editingId}
                        className={codigoErro ? "border-red-500" : ""}
                        data-testid="input-codigo"
                      />
                      <p className="text-[10px] text-slate-500 mt-1">4 caracteres alfanuméricos únicos</p>
                      {codigoErro && <p className="text-[11px] text-red-600 mt-0.5">{codigoErro}</p>}
                    </div>
                    <div className="md:col-span-2">
                      <Label>Nome do material <span className="text-red-500">*</span></Label>
                      <Input
                        value={form.nome}
                        onChange={(e) => setForm({ ...form, nome: e.target.value })}
                        placeholder="Ex: Tinta acrílica branca premium"
                        data-testid="input-nome"
                      />
                    </div>
                    <div>
                      <Label>Tipo <span className="text-red-500">*</span></Label>
                      <NativeSelect
                        value={form.tipo_material}
                        onValueChange={(v) => setForm({ ...form, tipo_material: v })}
                        options={tipos.map((t) => ({ value: t, label: t }))}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Controle</Label>
                    <NativeSelect
                      value={form.controle}
                      onValueChange={(v) => setForm({ ...form, controle: v })}
                      data-testid="select-controle"
                      options={[
                        { value: "consumivel", label: "Consumível (baixa na saída — padrão)" },
                        { value: "retornavel", label: "Retornável (volta da obra: trena, marreta, balde…)" },
                      ]}
                    />
                  </div>
                  <div>
                    <Label>Descrição</Label>
                    <Textarea
                      value={form.descricao}
                      onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                      placeholder="Detalhes do material..."
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 space-y-4">
                  <h3 className="text-sm font-semibold text-luvcorp-navy uppercase tracking-wide flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-luvcorp-navy text-white text-xs flex items-center justify-center">2</span>
                    Fornecedor e preço
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Fornecedor principal <span className="text-red-500">*</span></Label>
                      <NativeSelect
                        value={form.fornecedor_id}
                        onValueChange={(v) => setForm({ ...form, fornecedor_id: v })}
                        placeholder="Selecione..."
                        options={[
                          { value: "", label: "— Selecione —" },
                          ...fornecedores.map((f) => ({ value: f.id, label: f.nome })),
                        ]}
                      />
                    </div>
                    <div>
                      <Label>Fornecedor secundário</Label>
                      <NativeSelect
                        value={form.fornecedor_secundario_id}
                        onValueChange={(v) => setForm({ ...form, fornecedor_secundario_id: v })}
                        placeholder="Opcional"
                        options={[
                          { value: "", label: "— Nenhum —" },
                          ...fornecedores
                            .filter((f) => f.id !== form.fornecedor_id)
                            .map((f) => ({ value: f.id, label: f.nome })),
                        ]}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label>Preço unitário (R$) <span className="text-red-500">*</span></Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={form.valor_unitario}
                        onChange={(e) => setForm({ ...form, valor_unitario: parseFloat(e.target.value) || 0 })}
                        data-testid="input-valor-unitario"
                      />
                    </div>
                    <div>
                      <Label>Unidade de medida <span className="text-red-500">*</span></Label>
                      <NativeSelect
                        value={form.unidade_medida_id}
                        onValueChange={(v) => setForm({ ...form, unidade_medida_id: v })}
                        placeholder="Selecione..."
                        options={[
                          { value: "", label: "— Selecione —" },
                          ...unidades.map((u) => ({ value: u.id, label: `${u.sigla || ""} - ${u.descricao}` })),
                        ]}
                      />
                    </div>
                    <div>
                      <Label>Localização</Label>
                      <Input
                        value={form.localizacao}
                        onChange={(e) => setForm({ ...form, localizacao: e.target.value })}
                        placeholder="Ex: Galpão A - Prateleira 3"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 space-y-4">
                  <h3 className="text-sm font-semibold text-luvcorp-navy uppercase tracking-wide flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-luvcorp-navy text-white text-xs flex items-center justify-center">3</span>
                    Controle de estoque
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Estoque mínimo <span className="text-red-500">*</span></Label>
                      <Input
                        type="number" step="1" min="0"
                        value={form.quantidade_minima}
                        onChange={(e) => setForm({ ...form, quantidade_minima: parseFloat(e.target.value) || 0 })}
                        data-testid="input-qtd-minima"
                      />
                    </div>
                    <div>
                      <Label>{editingId ? "Estoque atual (somente leitura)" : "Estoque inicial"}</Label>
                      <Input
                        type="number" step="1" min="0"
                        value={form.quantidade_atual}
                        onChange={(e) => setForm({ ...form, quantidade_atual: parseFloat(e.target.value) || 0 })}
                        disabled={!!editingId}
                      />
                      {editingId && (
                        <p className="text-[10px] text-slate-500 mt-1">
                          Para atualizar, use {'"Movimentações"'}.
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>Observações</Label>
                    <Textarea
                      value={form.observacoes}
                      onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                      placeholder="Anotações internas..."
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end gap-2 sticky bottom-0 bg-white/90 backdrop-blur py-3 border-t">
                <Button type="button" variant="outline" onClick={() => { setTab("lista"); setEditingId(null); setForm(emptyMaterial()); }}>
                  <X className="h-4 w-4 mr-2" /> Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving || !!codigoErro}
                  className="bg-luvcorp-navy hover:bg-luvcorp-navy/90 text-white"
                  data-testid="btn-salvar-material"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Cadastrar material"}
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* ============================== MOVIMENTAÇÕES ============================== */}
          <TabsContent value="movimentacoes" className="space-y-3">
            <MovimentacoesPanel
              materiais={items}
              obras={obras}
              users={users}
              onSaved={fetchData}
            />
          </TabsContent>

          {/* ============================== HISTÓRICO ============================== */}
          <TabsContent value="historico" className="space-y-3">
            <HistoricoPanel materiais={items} />
          </TabsContent>
        </Tabs>

        {/* DIALOG: Nova Unidade (apenas quick-add, mantido como diálogo por ser simples) */}
        <UnidadeQuickDialog
          open={showUnidadeDialog}
          onClose={() => setShowUnidadeDialog(false)}
          onSaved={fetchData}
        />

        {/* DIALOG: Relatório PDF de movimentações */}
        <RelatorioEstoqueDialog
          open={showRelatorio}
          onClose={() => setShowRelatorio(false)}
          obras={obras}
        />
      </div>
    </MainLayout>
  );
}

// ============================================================
// MOVIMENTAÇÕES — Entrada por NF + aviso de saída via Romaneio + ajuste de inventário
// ============================================================
function MovimentacoesPanel({ materiais, obras, users, onSaved }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <EntradaNFForm materiais={materiais} onSaved={onSaved} />
      <SaidaAjustePanel materiais={materiais} onSaved={onSaved} />
    </div>
  );
}

function EntradaForm({ materiais, onSaved }) {
  const [form, setForm] = useState({
    material_id: "",
    quantidade: 1,
    valor_unitario: 0,
    data_movimentacao: new Date().toISOString().slice(0, 10),
    nota_fiscal: "",
    motivo: "",
  });
  const [saving, setSaving] = useState(false);

  const mat = materiais.find((m) => m.id === form.material_id);
  const subtotal = form.quantidade * form.valor_unitario;

  const handleMaterialChange = (v) => {
    const found = materiais.find((m) => m.id === v);
    setForm((f) => ({
      ...f,
      material_id: v,
      valor_unitario: found?.valor_unitario || 0,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.material_id) return toast.error("Selecione o material");
    if (form.quantidade <= 0) return toast.error("Quantidade deve ser > 0");
    if (form.valor_unitario <= 0) return toast.error("Preço unitário deve ser > 0");
    if (form.data_movimentacao > new Date().toISOString().slice(0, 10))
      return toast.error("Data não pode ser futura");

    setSaving(true);
    try {
      await axios.post(
        `${API}/estoque/movimentacao`,
        {
          material_id: form.material_id,
          tipo: "entrada",
          quantidade: form.quantidade,
          valor_unitario: form.valor_unitario,
          data_movimentacao: form.data_movimentacao,
          nota_fiscal: form.nota_fiscal,
          motivo: form.motivo,
        },
        { headers: authHeaders() }
      );
      toast.success("Entrada registrada");
      setForm({
        material_id: "",
        quantidade: 1,
        valor_unitario: 0,
        data_movimentacao: new Date().toISOString().slice(0, 10),
        nota_fiscal: "",
        motivo: "",
      });
      onSaved();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao registrar entrada"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-l-4 border-l-green-500" data-testid="form-entrada">
      <CardContent className="p-4 space-y-3">
        <h3 className="font-semibold flex items-center gap-2 text-green-700">
          <ArrowUpCircle className="h-5 w-5" /> Entrada de material (compra)
        </h3>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Material <span className="text-red-500">*</span></Label>
            <NativeSelect
              value={form.material_id}
              onValueChange={handleMaterialChange}
              placeholder="Selecione..."
              options={[
                { value: "", label: "— Selecione —" },
                ...materiais.map((m) => ({ value: m.id, label: `${m.codigo} • ${m.nome}` })),
              ]}
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Qtd <span className="text-red-500">*</span></Label>
              <Input
                type="number" min="0.01" step="0.01"
                value={form.quantidade}
                onChange={(e) => setForm({ ...form, quantidade: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Preço Unit. <span className="text-red-500">*</span></Label>
              <Input
                type="number" min="0.01" step="0.01"
                value={form.valor_unitario}
                onChange={(e) => setForm({ ...form, valor_unitario: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Data <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={form.data_movimentacao}
                onChange={(e) => setForm({ ...form, data_movimentacao: e.target.value })}
                max={new Date().toISOString().slice(0, 10)}
              />
            </div>
          </div>
          <div>
            <Label>Nota Fiscal (ref.)</Label>
            <Input
              value={form.nota_fiscal}
              onChange={(e) => setForm({ ...form, nota_fiscal: e.target.value })}
              placeholder="Número da NF (opcional)"
            />
          </div>
          <div>
            <Label>Observações</Label>
            <Textarea
              value={form.motivo}
              onChange={(e) => setForm({ ...form, motivo: e.target.value })}
              rows={2}
            />
          </div>
          {mat && (
            <div className="bg-green-50 p-2 rounded text-sm flex justify-between">
              <span className="text-slate-600">Subtotal:</span>
              <span className="font-semibold">{fmtBRL(subtotal)}</span>
            </div>
          )}
          <Button type="submit" disabled={saving} className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="btn-salvar-entrada">
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Registrando..." : "Registrar Entrada"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function SaidaForm({ materiais, obras, users, onSaved }) {
  const [form, setForm] = useState({
    material_id: "",
    quantidade: 1,
    data_movimentacao: new Date().toISOString().slice(0, 10),
    contrato_id: "",
    responsavel_id: "",
    motivo: "",
  });
  const [saving, setSaving] = useState(false);

  const mat = materiais.find((m) => m.id === form.material_id);
  const insuficiente = mat && form.quantidade > mat.quantidade_atual;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.material_id) return toast.error("Selecione o material");
    if (form.quantidade <= 0) return toast.error("Quantidade deve ser > 0");
    if (insuficiente) return toast.error(`Estoque insuficiente (disponível: ${fmtQtd(mat.quantidade_atual)})`);
    if (!form.contrato_id) return toast.error("Selecione a obra (centro de custo)");
    if (!form.responsavel_id) return toast.error("Selecione o responsável");
    if (form.data_movimentacao > new Date().toISOString().slice(0, 10))
      return toast.error("Data não pode ser futura");

    setSaving(true);
    try {
      await axios.post(
        `${API}/estoque/movimentacao`,
        {
          material_id: form.material_id,
          tipo: "saida",
          quantidade: form.quantidade,
          data_movimentacao: form.data_movimentacao,
          contrato_id: form.contrato_id,
          responsavel_id: form.responsavel_id,
          motivo: form.motivo,
        },
        { headers: authHeaders() }
      );
      toast.success("Saída registrada");
      setForm({
        material_id: "",
        quantidade: 1,
        data_movimentacao: new Date().toISOString().slice(0, 10),
        contrato_id: "",
        responsavel_id: "",
        motivo: "",
      });
      onSaved();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao registrar saída"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-l-4 border-l-red-500" data-testid="form-saida">
      <CardContent className="p-4 space-y-3">
        <h3 className="font-semibold flex items-center gap-2 text-red-700">
          <ArrowDownCircle className="h-5 w-5" /> Saída de material (uso em obra)
        </h3>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Material <span className="text-red-500">*</span></Label>
            <NativeSelect
              value={form.material_id}
              onValueChange={(v) => setForm({ ...form, material_id: v })}
              placeholder="Selecione..."
              searchable
              options={[
                { value: "", label: "— Selecione —" },
                ...materiais.map((m) => ({
                  value: m.id,
                  label: `${m.codigo} • ${m.nome} (estoque: ${fmtQtd(m.quantidade_atual)})`,
                })),
              ]}
            />
            {mat && mat.quantidade_atual === 0 && (
              <p className="text-[11px] text-red-600 mt-1">⚠ Material sem estoque disponível</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Quantidade <span className="text-red-500">*</span></Label>
              <Input
                type="number" min="0.01" step="0.01"
                value={form.quantidade}
                onChange={(e) => setForm({ ...form, quantidade: parseFloat(e.target.value) || 0 })}
                className={insuficiente ? "border-red-500" : ""}
              />
              {insuficiente && (
                <p className="text-[11px] text-red-600 mt-0.5">Estoque insuficiente (disp.: {mat?.quantidade_atual})</p>
              )}
            </div>
            <div>
              <Label>Data <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={form.data_movimentacao}
                onChange={(e) => setForm({ ...form, data_movimentacao: e.target.value })}
                max={new Date().toISOString().slice(0, 10)}
              />
            </div>
          </div>
          <div>
            <Label>Obra / Centro de Custo <span className="text-red-500">*</span></Label>
            <NativeSelect
              value={form.contrato_id}
              onValueChange={(v) => setForm({ ...form, contrato_id: v })}
              placeholder={obras.length ? "Selecione a obra..." : "Sem obras ativas"}
              options={[
                { value: "", label: obras.length ? "— Selecione —" : "Nenhum contrato ativo" },
                ...obras.map((o) => ({
                  value: o.id,
                  label: `${o.titulo}${o.cliente_nome ? " — " + o.cliente_nome : ""}`,
                })),
              ]}
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Apenas contratos com status {'"Ativo"'} aparecem aqui.
            </p>
          </div>
          <div>
            <Label>Responsável pelo pedido <span className="text-red-500">*</span></Label>
            <NativeSelect
              value={form.responsavel_id}
              onValueChange={(v) => setForm({ ...form, responsavel_id: v })}
              placeholder="Selecione..."
              options={[
                { value: "", label: "— Selecione —" },
                ...users.map((u) => ({ value: u.id, label: u.name })),
              ]}
            />
          </div>
          <div>
            <Label>Observações</Label>
            <Textarea
              value={form.motivo}
              onChange={(e) => setForm({ ...form, motivo: e.target.value })}
              rows={2}
            />
          </div>
          <Button
            type="submit"
            disabled={saving || insuficiente}
            className="w-full bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            data-testid="btn-salvar-saida"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Registrando..." : "Registrar Saída"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ============================================================
// HISTÓRICO — visualização filtrada de movimentações
// ============================================================
function HistoricoPanel({ materiais }) {
  const [materialId, setMaterialId] = useState("");
  const [tipo, setTipo] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const buscar = async () => {
    if (!materialId) {
      toast.error("Selecione um material");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tipo) params.append("tipo", tipo);
      if (dataInicio) params.append("data_inicio", dataInicio);
      if (dataFim) params.append("data_fim", dataFim);
      const r = await axios.get(
        `${API}/estoque/${materialId}/historico?${params.toString()}`,
        { headers: authHeaders() }
      );
      setData(r.data);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao carregar histórico"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card>
        <CardContent className="p-3 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs">Material</Label>
            <NativeSelect
              value={materialId}
              onValueChange={setMaterialId}
              placeholder="Selecione..."
              options={[
                { value: "", label: "— Selecione um material —" },
                ...materiais.map((m) => ({ value: m.id, label: `${m.codigo} • ${m.nome}` })),
              ]}
            />
          </div>
          <div>
            <Label className="text-xs">Tipo</Label>
            <NativeSelect
              value={tipo}
              onValueChange={setTipo}
              options={[
                { value: "", label: "Todos" },
                { value: "entrada", label: "Entrada" },
                { value: "saida", label: "Saída" },
              ]}
            />
          </div>
          <div>
            <Label className="text-xs">De</Label>
            <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
          </div>
          <div className="md:col-span-5 flex justify-end">
            <Button onClick={buscar} disabled={loading || !materialId} data-testid="btn-buscar-historico">
              <Filter className="h-4 w-4 mr-2" />
              {loading ? "Buscando..." : "Filtrar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-3 bg-slate-50 border-b">
              <p className="font-semibold">{data.material.codigo} — {data.material.nome}</p>
              <p className="text-xs text-slate-500">
                Estoque atual: <b>{data.material.quantidade_atual}</b> • Movimentações encontradas: {data.movimentacoes.length}
              </p>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Qtde</TableHead>
                  <TableHead className="text-right">Estoque após</TableHead>
                  <TableHead>Origem/Destino</TableHead>
                  <TableHead>Responsável</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Observação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.movimentacoes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                      Nenhuma movimentação encontrada nos filtros aplicados.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.movimentacoes.map((mov) => (
                    <TableRow key={mov.id}>
                      <TableCell className="text-xs">{fmtDateTime(mov.data_movimentacao || mov.created_at)}</TableCell>
                      <TableCell>
                        <Badge className={mov.tipo === "entrada" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                          {mov.tipo === "entrada" ? "Entrada" : "Saída"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">{mov.quantidade}</TableCell>
                      <TableCell className="text-right font-mono text-slate-600">{mov.quantidade_posterior}</TableCell>
                      <TableCell className="text-xs">
                        {mov.tipo === "entrada"
                          ? mov.nota_fiscal ? `NF: ${mov.nota_fiscal}` : "Compra"
                          : mov.contrato_titulo || "—"}
                      </TableCell>
                      <TableCell className="text-xs">{mov.responsavel_nome || "—"}</TableCell>
                      <TableCell className="text-right text-xs">{fmtBRL(mov.valor_total || (mov.valor_unitario || 0) * mov.quantidade)}</TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-[200px] truncate" title={mov.motivo}>{mov.motivo || "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </>
  );
}

// ============================================================
// Auxiliares
// ============================================================
function KPI({ icon: Icon, label, value, color, small }) {
  const colors = {
    blue: "bg-blue-100 text-blue-700",
    red: "bg-red-100 text-red-700",
    green: "bg-green-100 text-green-700",
    purple: "bg-purple-100 text-purple-700",
  };
  return (
    <Card>
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color] || colors.blue}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</p>
          <p className={`font-bold text-luvcorp-navy ${small ? "text-base" : "text-2xl"}`}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function UnidadeQuickDialog({ open, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ sigla: "", descricao: "", tipo: "quantidade" });

  const submit = async (e) => {
    e.preventDefault();
    if (!data.sigla || !data.descricao) return toast.error("Preencha sigla e descrição");
    setSaving(true);
    try {
      await axios.post(`${API}/unidades-medida`, data, { headers: authHeaders() });
      toast.success("Unidade criada");
      setData({ sigla: "", descricao: "", tipo: "quantidade" });
      onSaved();
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao salvar"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nova unidade de medida</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sigla *</Label>
              <Input value={data.sigla} onChange={(e) => setData({ ...data, sigla: e.target.value.toUpperCase() })} placeholder="KG" />
            </div>
            <div>
              <Label>Tipo</Label>
              <NativeSelect
                value={data.tipo}
                onValueChange={(v) => setData({ ...data, tipo: v })}
                options={[
                  { value: "quantidade", label: "Quantidade" },
                  { value: "volume", label: "Volume" },
                  { value: "peso", label: "Peso" },
                  { value: "comprimento", label: "Comprimento" },
                  { value: "area", label: "Área" },
                ]}
              />
            </div>
          </div>
          <div>
            <Label>Descrição *</Label>
            <Input value={data.descricao} onChange={(e) => setData({ ...data, descricao: e.target.value })} placeholder="Quilograma" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-luvcorp-navy">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// EXPORT
// ============================================================
export default function EstoquePage({ modo = "consumivel" }) {
  return (
    <Routes>
      <Route index element={<EstoqueMain modo={modo} />} />
    </Routes>
  );
}
